import type { DragEndEvent } from "@dnd-kit/core";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { BookingStatus, ClockTypes, Status } from "@repo/database";
import {
  DEFAULT_BOOKING_DURATION,
  groupBy,
  isBetween,
  nearestThirtyMinutes,
} from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import {
  addDays,
  addMinutes,
  compareAsc,
  differenceInMinutes,
  differenceInSeconds,
  endOfDay,
  format,
  isBefore,
  isSameDay,
  set,
} from "date-fns";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import type { Booking, PartialBooking } from "~/providers/booking-provider";
import type { Table } from "~/providers/table-provider";
import BookingItem from "~/components/booking/booking-item";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useBooking } from "~/hooks/use-booking";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useSection } from "~/hooks/use-section";
import { useShift } from "~/hooks/use-shift";
import { useTable } from "~/hooks/use-table";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { cn, getMatchedShifts } from "~/lib/utils";
import { useBookingManager } from "~/managers/use-booking-manager";
import {
  useBookingStore,
  useTempBookingStore,
} from "~/stores/use-booking-store";

export const Route = createFileRoute("/_main/bookings/_layout/index copy")({
  component: Bookings,
});

const getValidCombinations = (
  neighbors: Table[],
  remainingSeats: number,
): Table[][] => {
  const result: Table[][] = [];

  const findCombinations = (
    index: number,
    currentCombo: Table[],
    currentSeats: number,
  ) => {
    if (currentSeats >= remainingSeats) {
      result.push(currentCombo);
      return;
    }

    for (let i = index; i < neighbors.length; i++) {
      findCombinations(
        i + 1,
        [...currentCombo, neighbors[i]],
        currentSeats + neighbors[i].maxSeats,
      );
    }
  };

  findCombinations(0, [], 0);
  return result;
};

function TableBooking({
  booking,
  posX,
  onClick,
}: {
  booking: PartialBooking;
  posX: number;
  onClick: () => void;
}) {
  const { setNodeRef, transform, listeners, attributes } = useDraggable({
    id: booking.id ?? "TEMP",
  });

  const { t } = useTranslation("common");

  const { selectedDate } = useTempBookingStore();

  const { tables } = useTable();

  const tablesOnBooking = tables.filter((table) =>
    booking.tableIds?.includes(table.id),
  );

  const seats = tablesOnBooking.reduce((sum, table) => sum + table.seats, 0);

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  // Calculate the height based on the number of tables the booking spans
  const height = (booking.tableIds?.length ?? 1) * 36; // Assuming each table row is 36px tall

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      {...listeners}
      {...attributes}
      ref={(el) => {
        setNodeRef(el);
      }}
      style={{
        ...style,
        left: posX,
        width: differenceInMinutes(booking.endTime, booking.startTime) * 2,
        height: `${height - 4}px`, // Dynamic height
      }}
      className={cn(
        "absolute top-[2px] z-[95] flex cursor-pointer whitespace-nowrap  text-white",
        booking.bookingStatus === BookingStatus.active && "bg-blue-500",
        booking.bookingStatus === BookingStatus.checked_in && "bg-green-500",
        booking.bookingStatus === BookingStatus.no_show && "bg-red-500",
        booking.bookingStatus === BookingStatus.canceled && "bg-gray-500",
        isBefore(endOfDay(selectedDate), new Date()) && "bg-gray-500",
        !booking.id && "bg-yellow-500",
      )}
    >
      <div
        className={cn(
          "flex items-center  p-2 font-bold",
          booking.bookingStatus === BookingStatus.active && "bg-blue-800",
          booking.bookingStatus === BookingStatus.checked_in && "bg-green-800",
          booking.bookingStatus === BookingStatus.no_show && "bg-red-800",
          booking.bookingStatus === BookingStatus.canceled && "bg-gray-800",
          isBefore(endOfDay(selectedDate), new Date()) && "bg-gray-800",
          !booking.id && "bg-yellow-800",
        )}
      >
        {booking.groupSize}
      </div>

      <div className="flex flex-row gap-2 flex-1 justify-between items-center px-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center flex-1 text-sm">
              {booking.firstName
                ? `${booking.firstName} ${booking.lastName}`
                : "Walk in"}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <ul>
              <li>
                {t("Table.table_x", {
                  x: tables
                    .filter((table) => booking.tableIds?.includes(table.id))
                    .sort((a, b) => a.position - b.position)
                    .map((table) => table.reference)
                    .join(", "),
                })}
              </li>
              <li>
                {t("Booking.group_name_group_size", {
                  group_name: booking.firstName ?? "Walk in",
                  group_size: booking.groupSize,
                })}
              </li>
              <li>{`${format(booking.startTime, "p")} - ${format(booking.endTime, "p")}`}</li>
            </ul>
          </TooltipContent>
        </Tooltip>

        {seats < booking.groupSize && (
          <Tooltip>
            <TooltipTrigger>
              <AlertCircle size={18} />
            </TooltipTrigger>
            <TooltipContent>
              <p>Group size is larger than available seats</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function TableRow({
  table,
  bookings,
  className,
}: {
  table: Table;
  bookings: Booking[];
  className?: string;
}) {
  const timelineRef = useRef<HTMLDivElement | null>(null);

  const { openBookingDialog, createTempBooking } = useBookingManager();

  const { bookingDialogOpen, tempBooking, selectedDate, setTempBooking } =
    useTempBookingStore(
      useShallow((state) => ({
        bookingDialogOpen: state.bookingDialogOpen,
        tempBooking: state.tempBooking,
        setTempBooking: state.setTempBooking,
        selectedDate: state.selectedDate,
      })),
    );

  const start = set(new Date(), {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const { setNodeRef } = useDroppable({
    id: table.id,
  });

  useEffect(() => {
    if (bookingDialogOpen === false) {
      setTempBooking(undefined);
    }
  }, [bookingDialogOpen, setTempBooking]);

  return (
    <>
      <div
        role="none"
        onClick={(e) => {
          if (!timelineRef.current) return;

          // Get the bounding rect of the timeline element
          const { left } = timelineRef.current.getBoundingClientRect();

          // Calculate the X position relative to the timeline
          const x = e.clientX - left;

          // Assuming each 2 pixels corresponds to 1 minute (you can adjust this)
          const minutesFromStart = Math.floor(x / 2);

          // Calculate the total minutes from the start time
          const totalMinutes = start.getMinutes() + minutesFromStart;

          // Adjust the selected date to match the new time
          const startTime = nearestThirtyMinutes(
            set(selectedDate, {
              hours: start.getHours(),
              minutes: totalMinutes,
            }),
            "floor",
          );

          const booking: PartialBooking = {
            bookingStatus: BookingStatus.active,
            startTime,
            endTime: addMinutes(startTime, DEFAULT_BOOKING_DURATION),
            tableIds: [table.id],
            groupSize: table.maxSeats,
          };

          // Open the booking dialog with the newly calculated time
          void createTempBooking(booking);
          void openBookingDialog(booking);
        }}
        ref={(el) => {
          timelineRef.current = el;
          setNodeRef(el);
        }}
        className={cn(
          "timeline-row relative flex h-full w-full items-center border-b-black",
          className,
        )}
      >
        {tempBooking?.tableIds?.includes(table.id) && (
          <TableBooking
            onClick={() => {
              // void openBookingDialog(booking);
            }}
            booking={{
              ...tempBooking,
            }}
            posX={
              differenceInMinutes(
                tempBooking.startTime,
                set(start, {
                  date: tempBooking.startTime.getDate(),
                  month: tempBooking.startTime.getMonth(),
                  year: tempBooking.startTime.getFullYear(),
                }),
              ) * 2
            }
          />
        )}

        {bookings
          .filter((booking) => booking.status === Status.active)
          .map((booking) => (
            <TableBooking
              onClick={() => {
                void openBookingDialog(booking);
              }}
              key={booking.id}
              booking={booking}
              posX={
                differenceInMinutes(
                  booking.startTime,
                  set(start, {
                    date: booking.startTime.getDate(),
                    month: booking.startTime.getMonth(),
                    year: booking.startTime.getFullYear(),
                  }),
                ) * 2
              }
            />
          ))}
      </div>
    </>
  );
}

function TableRows({ tables }: { tables: Table[] }) {
  const { bookings } = useBooking();
  const { selectedDate } = useTempBookingStore();

  // Sort the tables by position, as you already do
  const sortedTables = tables.sort((a, b) => a.position - b.position);

  return (
    <div className="flex flex-col border">
      {sortedTables.map((table) => {
        // Get all bookings for this table on the selected date
        const tableBookings = bookings.filter(
          (booking) =>
            isSameDay(booking.startTime, selectedDate) &&
            booking.tableIds.includes(table.id),
        );

        return (
          <div
            className={cn(
              "relative group flex h-[36px] items-center justify-center bg-white dark:bg-bunker-950 text-black even:bg-purple-50 dark:even:bg-bunker-800",
            )}
            key={table.id}
          >
            <div className="sticky overflow-hidden flex-shrink-0 top-0 z-[96] left-0 flex h-full w-[120px] items-center justify-center group-last:border-b-0 border-b border-r bg-white dark:bg-bunker-950 dark:text-white text-black">
              <p className="truncate">{table.reference}</p>
              <div className="absolute right-1 top-1">
                <p className="text-xs">{table.maxSeats}</p>
              </div>
            </div>
            <TableRow
              className="border-b group-last:border-b-0"
              table={table}
              bookings={tableBookings.filter((booking) => {
                // Get the positions of the tables the booking is linked to
                const bookingTablePositions = booking.tableIds
                  .filter((id): id is string => !!id) // Filter undefined
                  .map((tableId) => {
                    const foundTable = sortedTables.find(
                      (t) => t.id === tableId,
                    );
                    return foundTable?.position ?? -1;
                  })
                  .sort((a, b) => a - b);

                // Determine the first table in the range
                const firstTablePosition = bookingTablePositions[0];

                // Only render if the current table is the first in the range
                return table.position === firstTablePosition;
              })}
            />
          </div>
        );
      })}
    </div>
  );
}

function TimeIndicator({
  scrollContainerRef,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const [time, setTime] = useState(new Date());
  const indicatorRef = useRef<HTMLDivElement>(null);
  const { selectedDate } = useTempBookingStore();

  const start = set(selectedDate, {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTime(new Date());
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (indicatorRef.current && scrollContainerRef.current) {
      const indicatorPosition =
        indicatorRef.current.getBoundingClientRect().left;

      scrollContainerRef.current.scrollTo({
        left: indicatorPosition * 0.8,
        behavior: "smooth",
      });
    }
  }, [scrollContainerRef, selectedDate]);
  return (
    <div
      ref={indicatorRef}
      style={{
        left:
          (differenceInSeconds(
            time,
            set(start, {
              date: time.getDate(),
              month: time.getMonth(),
              year: time.getFullYear(),
            }),
          ) /
            60) *
            2 +
          120,
      }}
      className="w-[4px] absolute h-full bg-red-500 z-[100] top-0"
    ></div>
  );
}

function StatusGroup({
  startTime,
  bookings,
  status,
  totalGuests,
  totalBookings,
}: {
  startTime: string;
  bookings: Booking[];
  status?: BookingStatus;
  totalGuests: number;
  totalBookings: number;
}) {
  const [parent] = useAutoAnimate();

  const { t } = useTranslation("booking");

  const { user } = useUser();

  return (
    <div ref={parent}>
      <div className="top-0 sticky z-30 flex h-10 w-full items-center gap-2 bg-black px-4 text-white">
        <span className="text-lg font-semibold">
          {status === BookingStatus.pending &&
            format(
              startTime,
              user.clock === ClockTypes.military
                ? "EEEEEE dd-MM-yyyy HH:mm"
                : "EEEEEE MM/dd/yyyy hh:aa a",
            )}
          {status !== BookingStatus.pending &&
            format(
              startTime,
              user.clock === ClockTypes.military ? "HH:mm" : "hh:aa a",
            )}
        </span>
        <div className="flex flex-row gap-1">
          <span>
            {t("DayView.Accordion.guests_label", {
              total_guests: totalGuests,
            })}
          </span>
          <span>/</span>
          <span>
            {t("DayView.Accordion.bookings_label", {
              total_bookings: totalBookings,
            })}
          </span>
        </div>
      </div>
      <div className="flex flex-col">
        {bookings.map((booking) => (
          <BookingItem
            booking={booking}
            key={booking.id}
            showProvider
            showStatus={
              !(!status || booking.bookingStatus === BookingStatus.pending)
            }
          />
        ))}
      </div>
    </div>
  );
}

function BookingsByStatus({
  bookings,
  status,
  label,
}: {
  bookings: Booking[];
  status?: BookingStatus;
  label: string;
}) {
  const { t } = useTranslation("booking");
  const [parent] = useAutoAnimate();

  const groupedEvents = groupBy(
    bookings.sort((a, b) => compareAsc(a.startTime, b.startTime)),
    "startTime",
  );

  const groupedEntries = Object.entries(groupedEvents);

  return (
    <AccordionItem value={status ?? "trashed"}>
      <AccordionTrigger
        className={cn(
          "px-4",
          status === BookingStatus.active && "bg-blue-600",
          status === BookingStatus.pending && "bg-yellow-600",
          status === BookingStatus.canceled && "bg-bunker-600",
          status === BookingStatus.checked_in && "bg-green-600",
          status === BookingStatus.no_show && "bg-red-600",
          status === undefined && "bg-red-800",
        )}
        headerclassname="sticky top-0 z-50"
      >
        <div className="flex items-center gap-2 text-white">
          <span className="text-lg font-semibold">{label}</span>
          <div className="flex flex-row gap-1">
            <span>
              {t("DayView.Accordion.guests_label", {
                total_guests: bookings.reduce(
                  (total, booking) => total + booking.groupSize,
                  0,
                ),
              })}
            </span>
            <span>/</span>
            <span>
              {t("DayView.Accordion.bookings_label", {
                total_bookings: bookings.length,
              })}
            </span>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-0">
        {bookings.length === 0 && (
          <div className="flex h-[100px] w-full items-center justify-center">
            {t("no_bookings")}
          </div>
        )}
        {bookings.length > 0 && (
          <div ref={parent}>
            {groupedEntries.map(([startTime, bookings]) => {
              const totalGuests = bookings.reduce(
                (totalGuests, booking) => totalGuests + booking.groupSize,
                0,
              );
              const totalBookings = bookings.length;
              return (
                <StatusGroup
                  key={startTime}
                  startTime={startTime}
                  bookings={bookings}
                  status={status}
                  totalGuests={totalGuests}
                  totalBookings={totalBookings}
                />
              );
            })}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function Guests() {
  const { t } = useTranslation("common");
  const { selectedDate } = useTempBookingStore();

  const { bookings } = useBooking();

  const statuses = [
    {
      value: BookingStatus.active,
      label: t("Booking.Status.planned"),
    },
    {
      value: BookingStatus.checked_in,
      label: t("Booking.Status.checked_in"),
    },
    {
      value: BookingStatus.no_show,
      label: t("Booking.Status.no_show"),
    },
    {
      value: BookingStatus.canceled,
      label: t("Booking.Status.canceled"),
    },
  ];
  return (
    <Accordion
      className="styled-scrollbar overflow-auto"
      defaultValue={[
        ...statuses.map((status) => status.value),
        BookingStatus.pending,
        Status.trashed,
      ]}
      type="multiple"
    >
      {/* {pendingBookings.length > 0 && (
        <BookingsByStatus
          bookings={pendingBookings}
          label={t("Status.pending")}
          status={BookingStatus.pending}
        />
      )} */}

      {statuses.map((status) => (
        <BookingsByStatus
          bookings={bookings.filter(
            (booking) =>
              booking.bookingStatus === status.value &&
              booking.status !== Status.trashed &&
              isSameDay(booking.startTime, selectedDate),
          )}
          key={status.value}
          label={status.label}
          status={status.value}
        />
      ))}

      {/* {trashedBookings.length > 0 && (
        <BookingsByStatus
          bookings={trashedBookings}
          label={t("Status.deleted")}
        />
      )} */}
    </Accordion>
  );
}

function Tables() {
  const { t } = useTranslation("common");
  const { bookings } = useBooking();

  const { openBookingDialog } = useBookingManager();

  const { selectedDate } = useTempBookingStore();

  const { shifts } = useShift();

  const isMobile = useMediaQuery(768);

  const { tables } = useTable();

  const { sections } = useSection();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { moveBooking } = useBookingManager();

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 100,
      distance: 5,
      tolerance: 100,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100,
      distance: 5,
      tolerance: 0,
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const matchedShifts = getMatchedShifts(shifts, selectedDate);

  const dayStart = set(selectedDate, {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const dayEnd = addDays(
    set(selectedDate, {
      hours: 2,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    }),
    1,
  );

  const timeBetween = differenceInMinutes(dayEnd, dayStart);

  const amountOfTimeslots = timeBetween / 60;

  const handleDragEnd = (event: DragEndEvent) => {
    const booking = bookings.find((b) => b.id === event.active.id);
    const targetTable = tables.find((t) => t.id === event.over?.id);

    if (!booking || !targetTable) {
      console.log("Booking or target table not found.");
      return;
    }

    const requiredSeats = booking.groupSize;
    let selectedTableIds = [targetTable.id];
    let accumulatedSeats = targetTable.seats;

    if (accumulatedSeats >= requiredSeats) {
      // If the target table alone can accommodate the booking
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
      return;
    }

    const remainingSeats = requiredSeats - accumulatedSeats;

    // Identify neighboring tables by position (adjacent)
    const currentTableRank = targetTable.position;
    const neighboringTables = tables
      .filter((table) => table.locationId === targetTable.locationId)
      .filter(
        (t) =>
          (t.position === currentTableRank - 1 ||
            t.position === currentTableRank + 1) &&
          t.id !== targetTable.id,
      );

    if (neighboringTables.length === 0) {
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
      return;
    }

    // Get valid combinations that meet or exceed the remaining seats
    const validCombinations = getValidCombinations(
      neighboringTables,
      remainingSeats,
    );

    if (validCombinations.length === 0) {
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
      return;
    }

    // Sort combinations to find the one with minimal total seats and fewer tables
    validCombinations.sort((a, b) => {
      const seatsA = a.reduce((sum, table) => sum + table.seats, 0);
      const seatsB = b.reduce((sum, table) => sum + table.seats, 0);
      return seatsA !== seatsB ? seatsA - seatsB : a.length - b.length;
    });

    const optimalCombination = validCombinations[0];
    const optimalCombinationIds = optimalCombination.map((t) => t.id);

    selectedTableIds = [targetTable.id, ...optimalCombinationIds];
    accumulatedSeats += optimalCombination.reduce(
      (sum, table) => sum + table.seats,
      0,
    );

    // Move the booking if we have sufficient seats
    if (accumulatedSeats >= requiredSeats) {
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
    } else {
      console.log("Not enough seats available in neighboring tables.");
    }
  };

  return (
    <DndContext
      autoScroll={{
        threshold: {
          x: 0,
          y: 0.2,
        },
      }}
      sensors={sensors}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <div
        ref={scrollContainerRef}
        className={cn(
          "flex flex-1 flex-col overflow-x-auto hide-scrollbar overscroll-none",
        )}
      >
        <div className="flex flex-col w-[2520px] sticky top-0 z-[99]">
          <div className="flex z-30 whitespace-nowrap border-b border-black dark:border-border h-10 w-full">
            <div className="bg-neutral-200 dark:bg-black text-neutral-600 h-full w-[120px]"></div>
            {Array.from({ length: amountOfTimeslots + 1 }).map((_, idx) => {
              const currentTime = addMinutes(dayStart, idx * 60);
              const isOpen = matchedShifts.some((shift) => {
                const shiftStart = set(selectedDate, {
                  hours: shift.startTime.getHours(),
                  minutes: shift.startTime.getMinutes(),
                  seconds: 0,
                  milliseconds: 0,
                });

                const shiftEnd = addMinutes(
                  set(selectedDate, {
                    hours: shift.endTime.getHours(),
                    minutes: shift.endTime.getMinutes(),
                    seconds: 0,
                    milliseconds: 0,
                  }),
                  shift.standardDuration,
                );

                const result = isBetween(currentTime, shiftStart, shiftEnd);

                return result;
              });

              return (
                <Fragment key={idx}>
                  <Button
                    onClick={() => {
                      void openBookingDialog({
                        startTime: currentTime,
                        endTime: addMinutes(
                          currentTime,
                          DEFAULT_BOOKING_DURATION,
                        ),
                        groupSize: 2,
                      });
                    }}
                    variant="ghost"
                    className={cn(
                      "timeline-slot rounded-none group before:shadow-dark-line before:h-[20%] before:w-[1px] before:bottom-0 before:absolute before:left-0 relative text-sm bg-neutral-200 dark:bg-black text-neutral-600 h-full w-[60px] flex items-center justify-center",
                      isOpen && "bg-white dark:bg-bunker-800",
                    )}
                  >
                    <div className="absolute transition-opacity flex inset-0 opacity-0 group-hover:opacity-100 justify-center items-center">
                      +
                    </div>
                    <span
                      className={cn(
                        "absolute left-0 top-0 -translate-x-1/2",
                        isOpen && "dark:text-white text-black",
                      )}
                    >
                      {format(currentTime, "HH:mm")}
                    </span>
                  </Button>
                  <Button
                    onClick={() => {
                      void openBookingDialog({
                        startTime: addMinutes(currentTime, 30),
                        endTime: addMinutes(
                          currentTime,
                          30 + DEFAULT_BOOKING_DURATION,
                        ),
                        groupSize: 2,
                      });
                    }}
                    variant="ghost"
                    className={cn(
                      "timeline-slot group rounded-none before:shadow-line dark:before:shadow-light-line before:h-[10%] before:w-[1px] before:bottom-0 before:absolute before:left-0 relative text-sm bg-neutral-200 dark:bg-black text-neutral-600 h-full w-[60px] flex items-center justify-center",
                      isOpen && "bg-white dark:bg-bunker-800",
                    )}
                  >
                    <div className="absolute transition-opacity flex inset-0 opacity-0 group-hover:opacity-100 justify-center items-center">
                      +
                    </div>
                  </Button>
                </Fragment>
              );
            })}
          </div>
          <div className="z-30 bg-white dark:bg-black flex flex-row border-b w-full">
            <div className="dark:bg-black bg-neutral-200 text-neutral-600 dark:text-white h-full w-[120px] flex items-center justify-center text-xs">
              {t("Booking.guests")}
            </div>
            {Array.from({ length: amountOfTimeslots * 2 }).map((_, idx) => {
              const currentTime = addMinutes(dayStart, idx * 30);

              const guestsAtTime = bookings
                .filter((booking) => {
                  const endTime = set(booking.endTime, {
                    hours: booking.endTime.getHours(),
                    minutes: booking.endTime.getMinutes() - 1,
                    seconds: 0,
                    milliseconds: 0,
                  });
                  return (
                    isBetween(currentTime, booking.startTime, endTime) &&
                    booking.bookingStatus !== BookingStatus.canceled &&
                    booking.bookingStatus !== BookingStatus.no_show
                  );
                })
                .reduce((acc, booking) => acc + booking.groupSize, 0);
              return (
                <Fragment key={idx}>
                  <div
                    className="relative odd:before:shadow-line dark:odd:before:shadow-light-line dark:even:before:shadow-light-line even:before:shadow-dark-line before:h-[100%] before:w-[1px] before:bottom-0 before:absolute before:left-0 h-6 w-[60px] flex items-center justify-center text-sm"
                    key={idx}
                  >
                    {guestsAtTime}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>

        <div className="relative w-[2520px] select-none flex flex-col">
          {isSameDay(selectedDate, new Date()) && (
            <TimeIndicator scrollContainerRef={scrollContainerRef} />
          )}
          {sections
            .sort((a, b) => a.position - b.position)
            .map((section) => (
              <div key={section.id}>
                <div className="sticky w-screen flex top-0 left-0 z-[98] bg-white dark:bg-black dark:text-white p-2 text-black">
                  <div className="left-0 w-max pl-4">
                    <span className="text-sm font-bold">{section.name}</span>
                  </div>
                </div>

                <TableRows
                  tables={tables.filter(
                    (table) => table.locationId === section.id,
                  )}
                />
              </div>
            ))}
        </div>
      </div>
      {!isMobile && <TableScrollBar scrollContainerRef={scrollContainerRef} />}

      {/* <DragOverlay
        modifiers={[restrictToFirstScrollableAncestor]}
      ></DragOverlay> */}
    </DndContext>
  );
}

function TableScrollBar({
  scrollContainerRef,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const dayStart = set(new Date(), {
    hours: 6,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const dayEnd = addDays(
    set(new Date(), {
      hours: 3,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    }),
    1,
  );

  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const customScrollBarRef = useRef<HTMLDivElement>(null);
  const animationFrameId = useRef<number | null>(null);
  const [startTime, setStartTime] = useState(dayStart);
  const [endTime, setEndTime] = useState(dayEnd);

  const calculateThumb = useCallback(() => {
    if (scrollContainerRef.current && customScrollBarRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const customScrollBar = customScrollBarRef.current;

      const { clientWidth, scrollWidth, scrollLeft } = scrollContainer;

      const visibleRatio = clientWidth / scrollWidth;

      const thumbWidth = Math.max(
        visibleRatio * customScrollBar.clientWidth,
        20,
      ); // Minimum width

      const maxThumbPosition = customScrollBar.clientWidth - thumbWidth;
      const thumbPosition =
        (scrollLeft / (scrollWidth - clientWidth)) * maxThumbPosition || 0;

      // Apply styles directly for better performance
      if (scrollThumbRef.current) {
        scrollThumbRef.current.style.width = `${thumbWidth}px`;
        scrollThumbRef.current.style.transform = `translateX(${thumbPosition}px)`;
      }
    }
  }, [scrollContainerRef]);

  const handleResize = useCallback(() => {
    if (animationFrameId.current !== null) {
      cancelAnimationFrame(animationFrameId.current);
    }
    animationFrameId.current = requestAnimationFrame(calculateThumb);
  }, [calculateThumb]);

  const handleScroll = useCallback(() => {
    if (animationFrameId.current !== null) {
      cancelAnimationFrame(animationFrameId.current);
    }

    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const clientWidth = scrollContainerRef.current.clientWidth;

      // Calculate the start X position relative to the timeline
      const startX = scrollLeft;

      // Assuming each 2 pixels corresponds to 1 minute (adjust as needed)
      const minutesFromStart = Math.floor(startX / 2);

      // Calculate the total minutes from the day start time
      const totalMinutesStart =
        dayStart.getHours() * 60 + dayStart.getMinutes() + minutesFromStart;

      // Update the start time
      setStartTime(
        set(dayStart, {
          hours: Math.floor(totalMinutesStart / 60) % 24, // Ensures hours wrap around 24
          minutes: totalMinutesStart % 60,
        }),
      );

      // Calculate the end X position by adding the visible width
      const endX = startX + clientWidth;
      const minutesFromStartEnd = Math.floor(endX / 2);

      // Calculate the total minutes for the end time
      const totalMinutesEnd =
        dayStart.getHours() * 60 + dayStart.getMinutes() + minutesFromStartEnd;

      // Update the end time
      setEndTime(
        set(dayStart, {
          hours: Math.floor(totalMinutesEnd / 60) % 24, // Ensures hours wrap around 24
          minutes: totalMinutesEnd % 60,
        }),
      );
    }

    // Schedule the next frame for thumb calculation
    animationFrameId.current = requestAnimationFrame(calculateThumb);
  }, [calculateThumb, dayStart, scrollContainerRef]);

  useEffect(() => {
    calculateThumb();

    window.addEventListener("resize", handleResize);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener("scroll", handleScroll);
      }
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [calculateThumb, handleResize, handleScroll, scrollContainerRef]);

  // Handle dragging of the scroll thumb
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    console.log(e);

    isDragging.current = true;
    startX.current = e.clientX;
    if (scrollContainerRef.current) {
      startScrollLeft.current = scrollContainerRef.current.scrollLeft;
    }
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (
      !isDragging.current ||
      !scrollContainerRef.current ||
      !customScrollBarRef.current
    )
      return;

    const deltaX = e.clientX - startX.current;

    const customScrollBar = customScrollBarRef.current;

    const thumbWidth = scrollThumbRef.current?.clientWidth ?? 20;

    const maxThumbPosition = customScrollBar.clientWidth - thumbWidth;

    const scrollRatio =
      (scrollContainerRef.current.scrollWidth -
        scrollContainerRef.current.clientWidth) /
      maxThumbPosition;

    scrollContainerRef.current.scrollLeft =
      startScrollLeft.current + deltaX * scrollRatio;
  };

  const handlePointerUp = () => {
    isDragging.current = false;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  };

  return (
    <div className="relative h-7 bg-background w-full border-t border-black">
      {/* Custom Scrollbar */}
      <div
        ref={customScrollBarRef}
        className="absolute top-0 left-0 h-full w-full bg-bunker-800"
      >
        {/* Scroll Thumb */}
        <div
          onPointerDown={handlePointerDown} // Start the drag
          ref={scrollThumbRef}
          className="h-full bg-background flex items-center justify-between px-4 rounded-full cursor-grab"
        >
          <span className="rounded-full px-1 bg-blue-400 text-xs h-4 flex items-center font-mono">
            {format(startTime, "p")}
          </span>
          <span className="rounded-full px-1 bg-blue-400 text-xs h-4 flex items-center font-mono">
            {format(endTime, "p")}
          </span>
        </div>
      </div>
    </div>
  );
}

function Bookings() {
  const { selectedView } = useBookingStore();

  const utils = api.useUtils();
  void utils.booking.getAll.invalidate();
  if (selectedView === "guests") {
    return <Guests />;
  } else {
    return <Tables />;
  }
}
